'use client'

import { useEffect, useRef } from 'react'
import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin'
import { TextPlugin } from 'gsap/TextPlugin'

gsap.registerPlugin(ScrollTrigger, SplitText, ScrambleTextPlugin, TextPlugin)

let lenisInstance: Lenis | null = null

export function getLenis(): Lenis | null {
  return lenisInstance
}

export function useSmoothScroll() {
  const lenis = useRef<Lenis | null>(null)

  useEffect(() => {
    // Accessibility: users who ask for reduced motion get native scrolling — no
    // Lenis lerp. ScrollTrigger falls back to native scroll events automatically,
    // and every getLenis() consumer guards for a null instance.
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) {
      gsap.ticker.lagSmoothing(0)
      return
    }

    // Create Lenis instance
    const instance = new Lenis({
      lerp: 0.1,
      smoothWheel: true,
      syncTouch: false,
    })

    // Sync Lenis with GSAP ScrollTrigger
    instance.on('scroll', ScrollTrigger.update)

    // Add Lenis raf to GSAP ticker
    const tick = (time: number) => {
      instance.raf(time * 1000)
    }
    gsap.ticker.add(tick)

    // Disable GSAP lag smoothing for precise scroll sync
    gsap.ticker.lagSmoothing(0)

    lenis.current = instance
    lenisInstance = instance
    // Dev-only handle for driving pinned sections during verification. Stripped in prod.
    if (process.env.NODE_ENV !== 'production') {
      ;(window as unknown as { __lenis?: Lenis }).__lenis = instance
    }

    return () => {
      gsap.ticker.remove(tick)
      instance.destroy()
      lenisInstance = null
    }
  }, [])

  return lenis
}
